import React, { useState, useEffect } from 'react';
import SEO from '../SEO/SEO';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { CheckCircle, User, Briefcase, DollarSign, Ruler, FileText, Globe, Star, Shield, Home, ExpandIcon, BadgeCheckIcon } from "lucide-react";
import {
  HomeIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BanknotesIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/solid';
import * as SolidIcons from '@heroicons/react/24/solid';
import * as OutlineIcons from '@heroicons/react/24/outline';
import * as LucideIcons from "lucide-react";

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';

const HomeExtensionLoan = () => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [activeTab, setActiveTab] = useState('common');
  const [activeFAQ, setActiveFAQ] = useState(0);  // OPEN FIRST FAQ BY DEFAULT
  const [activeCalc, setActiveCalc] = useState("overview");

  // Banner state
  const [banner, setBanner] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);

  // Fallback static banner data
  const fallbackBanner = {
    title: 'Expand Your Home Comfortably with Aham Home Extension Loan',
    subtitle: 'Add extra space for your growing needs with flexible funding and smooth construction-linked disbursement.',
    image_url: 'img/loan-banners/Home-Extension-Banner.webp',
    primary_button_text: 'Apply Now',
    primary_button_link: 'https://aham.mbwhost.in/contact',
    highlights: []
  };


  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
  };

  // Key Benefits Data
  const benefits = [
    {
      icon: HomeIcon,
      title: "Loan up to ₹15 Lakhs",
      description: "Finance additional rooms or extended living areas to increase your home’s comfort."
    },
    {
      icon: ExpandIcon,
      title: "Expand Your Existing Space",
      description: "Add bedrooms, study rooms, reading corners, balconies, or utility areas as per your needs."
    },
    {
      icon: ClockIcon,
      title: "Flexible Tenure up to 20 Years",
      description: "Choose a repayment schedule that fits your long-term financial plan."
    },
    {
      icon: ChartBarIcon,
      title: "Construction-Linked Disbursement",
      description: "Funds released based on approved project milestones and technical progress."
    },
    {
      icon: ShieldCheckIcon,
      title: "Maximum LTV up to 60%",
      description: "Get financial support based on the current property value and extension cost."
    },
    {
      icon: BadgeCheckIcon,
      title: "Minimal Documentation & Quick Approval",
      description: "Fast and smooth processing for eligible homeowners with clear property ownership."
    }
  ];


  // Eligibility Criteria
  const eligibility = [
    "Applicant must be the legal owner of the existing residential unit",
    "Available for Salaried, Self-Employed, and Business Professionals",
    "Age: 21 years minimum at loan origination",
    "Maximum age at loan maturity: 60 years (salaried) / 65 years (self-employed)",
    "Stable income with repayment capacity as per Aham policy",
    "Property must have clear title and be structurally suitable for extension",
    //"Extension plan must comply with local authority norms and approvals"
  ];

  // Matching icons & colors for each item
  const icons = [
    { icon: <User />, from: "from-blue-500", to: "to-cyan-400" },
    { icon: <Globe />, from: "from-green-500", to: "to-lime-400" },
    { icon: <Briefcase />, from: "from-pink-500", to: "to-rose-400" },
    { icon: <DollarSign />, from: "from-orange-500", to: "to-amber-400" },
    { icon: <FileText />, from: "from-purple-500", to: "to-indigo-400" },
    { icon: <Shield />, from: "from-teal-500", to: "to-emerald-400" },
    { icon: <Star />, from: "from-yellow-500", to: "to-orange-400" },
    { icon: <Ruler />, from: "from-red-500", to: "to-orange-400" },
  ];

  // Documents Required
  const documents = {
    common: [
      "Loan application form with passport-size photographs",
      "Identity Proof – Aadhaar, PAN, Passport, Driving License",
      "Address Proof – Aadhaar, Passport, Utility Bills, Rent Agreement",
      "Bank statements for the last 6 months"
    ],

    salaried: [
      "Last 3 months salary slips",
      "6 months bank statement showing salary credits",
      "Form 16 or IT Returns (last 2 years)",
      'Employee ID Card'
    ],

    selfEmployed: [
      "Business registration proof – GST, TIN, Trade License",
      "Audited financial statements / ITR (last 2–3 years)",
      "Business bank account statements (last 12 months)",
      "Business License / Trade License"
    ],

    nri: [
      "Valid passport and visa",
      "Overseas employment contract",
      "NRE/NRO bank statements (last 6 months)",
      "Power of Attorney (if applicable)"
    ],

    property: [
      "Ownership documents of existing house",
      "Approved extension plan / building approval",
      "Estimated cost of extension work from contractor/engineer",
      "Latest property tax receipts",
      "Technical & legal verification reports",
      "Utility bills for last 6 months"
    ]
  };


  // FAQs
  const faqs = [
    {
      question: "What is a Home Extension Loan?",
      answer:
        "A Home Extension Loan provides financial support to expand your existing home by adding new rooms or additional living space."
    },
    {
      question: "What kind of extensions are allowed?",
      answer:
        "Extensions such as extra bedrooms, study rooms, reading spaces, balconies, utility areas, and small expansions are allowed as long as they comply with local building norms."
    },
    {
      question: "What is the maximum loan amount available?",
      answer:
        "You can avail a loan amount between ₹5 lakhs and ₹15 lakhs depending on eligibility and property valuation."
    },
    {
      question: "What is the maximum LTV for extension loans?",
      answer:
        "Up to 60% LTV based on the value of the existing property and extension estimate."
    },
    {
      question: "How does loan disbursement work?",
      answer:
        "Disbursement is linked to construction progress and released in stages as per technical inspection."
    },
    {
      question: "Do I need to own the existing house?",
      answer:
        "Yes. The applicant must be the owner of the existing residential unit where the extension is planned."
    },
    {
      question: "What is the repayment tenure?",
      answer:
        "The loan tenure ranges from 1 year to a maximum of 20 years."
    }
  ];


  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };

  // Overview state
  const [overview, setOverview] = useState(null);

  // Benefits state
  const [benefitsList, setBenefitsList] = useState(null);
  const [benefitsSettings, setBenefitsSettings] = useState(null);

  // Eligibility state
  const [eligibilityList, setEligibilityList] = useState(null);
  const [eligibilitySettings, setEligibilitySettings] = useState(null);

  // Documents state
  const [documentsList, setDocumentsList] = useState(null);
  const [documentsSettings, setDocumentsSettings] = useState(null);

  // Interest Rates state
  const [interestRates, setInterestRates] = useState([]);
  const [interestSettings, setInterestSettings] = useState(null);

  // FAQ state
  const [faqsList, setFaqsList] = useState(null);
  const [faqSettings, setFaqSettings] = useState(null);

  // CTA Banner state
  const [ctaBanner, setCtaBanner] = useState(null);

  // Helper to resolve icon component
  const getIconComponent = (iconName) => {
    if (!iconName) return Star; // Default
    // Check Lucide
    if (LucideIcons[iconName]) return LucideIcons[iconName];
    // Check Heroicons Solid
    if (SolidIcons[iconName]) return SolidIcons[iconName];
    // Check Heroicons Outline
    if (OutlineIcons[iconName]) return OutlineIcons[iconName];
    return Star;
  };

  // Fetch banner, overview, and benefits data on component mount
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        setBannerLoading(true);
        const response = await axios.get('/api/admin/banners/page/home_extension_loan');
        const banners = response.data.data || [];

        if (banners.length > 0) {
          setBanner(banners[0]);
        } else {
          setBanner(fallbackBanner);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
        setBanner(fallbackBanner);
      } finally {
        setBannerLoading(false);
      }
    };

    const fetchOverview = async () => {
      try {
        const response = await axios.get('/api/admin/loan-overview/home_extension_loan');
        if (response.data.success && response.data.data) {
          setOverview(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching overview:', error);
        // Fallback is handled in render
      }
    };

    const fetchDynamicData = async () => {
      try {
        const [benefitsRes, benefitsSettingsRes, eligibilityRes, eligibilitySettingsRes] = await Promise.all([
          axios.get('/api/admin/key-benefits/home_extension_loan/active'),
          axios.get('/api/admin/key-benefits/settings/home_extension_loan'),
          axios.get('/api/admin/eligibility/home_extension_loan/active'),
          axios.get('/api/admin/eligibility/settings/home_extension_loan')
        ]);

        // Benefits
        if (benefitsRes.data.success) {
          setBenefitsList(benefitsRes.data.data);
        } else {
          setBenefitsList([]);
        }
        if (benefitsSettingsRes.data.success) {
          setBenefitsSettings(benefitsSettingsRes.data.data);
        }

        // Eligibility
        if (eligibilityRes.data.success) {
          setEligibilityList(eligibilityRes.data.data);
        } else {
          setEligibilityList([]);
        }
        if (eligibilitySettingsRes.data.success) {
          setEligibilitySettings(eligibilitySettingsRes.data.data);
        }

      } catch (error) {
        console.error('Error fetching dynamic data:', error);
      }
    };

    fetchBanner();
    fetchOverview();
    fetchDynamicData();

    // Fetch Documents Seperately or add to dynamic data? Adding to dynamic data is cleaner but let's separate for clarity or merge?
    // Let's merge logic above but I cannot edit the big block easily.
    // I'll add a separate fetch for documents to avoid touching the big block again and again.
    const fetchDocuments = async () => {
      try {
        const [docsRes, docsSettingsRes] = await Promise.all([
          axios.get('/api/admin/documents/home_extension_loan/active'),
          axios.get('/api/admin/documents/settings/home_extension_loan')
        ]);

        if (docsRes.data.success) {
          setDocumentsList(docsRes.data.data);
        } else {
          setDocumentsList([]);
        }

        if (docsSettingsRes.data.success && docsSettingsRes.data.data) {
          setDocumentsSettings(docsSettingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    };
    fetchDocuments();

    const fetchInterestRates = async () => {
      try {
        const [ratesRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/interest-rates/home_extension_loan/active'),
          axios.get('/api/admin/interest-rates/settings/home_extension_loan')
        ]);

        if (ratesRes.data.success) {
          setInterestRates(ratesRes.data.data);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setInterestSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching interest rates:', error);
      }
    };
    fetchInterestRates();

    const fetchFAQs = async () => {
      try {
        const [faqsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/faqs/home_extension_loan/active'),
          axios.get('/api/admin/faqs/settings/home_extension_loan')
        ]);

        if (faqsRes.data.success) {
          setFaqsList(faqsRes.data.data);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setFaqSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      }
    };
    fetchFAQs();

    const fetchCTABanner = async () => {
      try {
        const response = await axios.get('/api/admin/cta-banner/settings/home_extension_loan');
        if (response.data.success && response.data.data) {
          setCtaBanner(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching CTA banner:', error);
      }
    };
    fetchCTABanner();
  }, []);


  // Scroll Event Listener to auto-switch active tab
  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "benefits", "eligibility", "documents", "interest", "faq"];

      sections.forEach((sec) => {
        const element = document.getElementById(sec);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveCalc(sec);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const headerHeight = 120; // top navbar + sticky navbar height

    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;

    const offsetPosition = elementPosition - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };
            
             // Schema for SEO
  const extensionSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions across multiple customer segments in India.",
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/home-extension-loan/#webpage",
        "url": "https://ahamhfc.com/home-extension-loan",
        "name": "Home Extension Loan - Aham Housing Finance",
        "description": "Expand your home with additional construction using a home extension loan from Aham Housing Finance, with competitive rates and quick processing.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/home-extension-loan/#loan"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/home-extension-loan/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/loan-overview/Purchase-Overview-1768284245336-463247481.webp"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "LoanOrCredit",
        "@id": "https://ahamhfc.com/home-extension-loan/#loan",
        "name": "Home Extension Loan",
        "url": "https://ahamhfc.com/home-extension-loan",
        "image": {
          "@id": "https://ahamhfc.com/home-extension-loan/#primaryimage"
        },
        "description": "Finance additional construction and expansion of your existing home with a dedicated home extension loan.",
        "category": "Housing Finance",
        "loanType": "Home Extension Loan",
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "offeredBy": {
          "@id": "https://ahamhfc.com/#financialservice"
        },
        "audience": {
          "@type": "Audience",
          "audienceType": "Homeowners planning additional construction or expansion"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/home-extension-loan/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Home Extension Loan",
            "item": "https://ahamhfc.com/home-extension-loan"
          }
        ]
      }
    ]
  };




  return (
    <div className="min-h-screen bg-white">

      <SEO
        title="Home Extension Loan: Attractive Rates | Aham Housing Finance"
        description="Apply for a Home Extension Loan with Aham Housing Finance. Expand your home with attractive rates, easy EMIs, and quick approval."
        keywords="Aham home extension loan, Aham Housing Finance home extension loan, home extension loan, house extension loan, loan for home extension, home expansion loan, additional room loan, room addition loan, first floor construction loan, second floor construction loan, home extension finance, affordable home extension loan, low documentation home extension loan, no income proof home extension loan, self employed home extension loan, flexible EMI home extension loan, home improvement extension finance, residential extension loan, Aham housing extension finance, Aham home expansion loan"
        canonical="https://ahamhfc.com/home-extension-loan"
        ogTitle="Home Extension Loan: Attractive Rates | Aham Housing Finance"
        ogDescription="Apply for a home extension loan at attractive interest rates. Add more space to your home with easy EMIs & tax benefits. Check eligibility & apply with Aham today!"
       schema={extensionSchema}
           />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {bannerLoading ? (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-pulse">
                <div className="h-8 w-48 bg-white/20 rounded mx-auto mb-4"></div>
                <div className="h-4 w-64 bg-white/10 rounded mx-auto"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${banner?.image_url || 'img/loan-banners/Home-Extension-Banner.webp'})`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
            </div>

            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl"
              >
         

                <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
                  {banner?.title || "Expand Your Home Comfortably with Aham Home Extension Loan"}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium">
                  {banner?.subtitle || "Add extra space for your growing needs with flexible funding and smooth construction-linked disbursement."}
                </p>

                {/* Display highlights if available */}
                {banner?.highlights && banner.highlights.length > 0 && (
                  <div className="flex flex-wrap gap-4 mb-8">
                    {banner.highlights.map((highlight, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 shadow-lg"
                      >
                        <CheckCircleIcon className="h-4 w-4 text-yellow-400" />
                        <span className="text-white text-xs md:text-sm font-medium">{highlight}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <a href={banner?.primary_button_link || "https://aham.mbwhost.in/contact"} className="w-full sm:w-auto text-left">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0"
                    >
                      {banner?.primary_button_text || "Apply Now"}
                    </motion.button>
                  </a>

                  {banner?.secondary_button_text && (
                    <a href={banner.secondary_button_link || "#"} className="w-full sm:w-auto text-left">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-primary-600 text-white hover:bg-primary-700 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0 border border-white/20"
                      >
                        {banner.secondary_button_text}
                      </motion.button>
                    </a>
                  )}
                </div>
                
                       {/* Breadcrumb Navigation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex items-center space-x-2 mb-6"
                >
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">Home Extension Loan</span>
                </motion.div>
                
              </motion.div>
            </div>
          </>
        )}
      </section>


      {/* Sticky Finance Navigation */}
      <div className="hidden md:block w-full py-3 sticky top-[80px] z-50">
        <div className="container mx-auto px-1">

          <div className="w-full bg-white rounded-full shadow-lg flex flex-wrap justify-center gap-2 md:gap-6 py-3 px-4">

            {[
              { id: "overview", label: "Overview" },
              { id: "benefits", label: "Benefits" },
              { id: "eligibility", label: "Loan Eligibility Criteria" },
              { id: "documents", label: "Documents" },
              { id: "interest", label: "Interest" },
              { id: "faq", label: "FAQ" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveCalc(tab.id);
                  scrollToSection(tab.id); // ONLY this — smooth scroll with offset
                }}
                className={`
    px-6 py-2 rounded-full font-semibold transition-all 
    ${activeCalc === tab.id
                    ? "bg-yellow-400 text-blue-800 shadow-lg scale-105"
                    : "text-gray-500 hover:text-blue-700"
                  }
  `}
              >
                {tab.label}
              </button>

            ))}
          </div>
        </div>
      </div>


      {/* Overview Section */}
      <section id="overview" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center md:text-left">
                {overview?.title || 'Home Extension Loan'}
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  {overview?.description || 'As your family grows, your living space should grow with it. Aham Housing Finance offers a Home Extension Loan to help you add extra rooms or expand your living area without the need to relocate.'}
                </p>

                {overview?.features ? (
                  // Ensure features is an array, parsing if necessary
                  (typeof overview.features === 'string' ? JSON.parse(overview.features) : overview.features).length > 0 && (
                    <ul className="space-y-3">
                      {(typeof overview.features === 'string' ? JSON.parse(overview.features) : overview.features).map((feature, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )
                ) : (
                  // Fallback features if no data
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                      <span><strong>Loan from ₹5 lakhs to ₹15 lakhs</strong> – Ideal for mid-size home expansion projects.</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                      <span><strong>Tenure up to 20 years</strong> – Flexible EMI options for long-term convenience.</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                      <span><strong>Construction-linked disbursement</strong> – Funds released as per project progress.</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                      <span><strong>Maximum LTV of 60%</strong> – Loan value aligned with your property valuation.</span>
                    </li>
                  </ul>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={overview?.image_url || "img/loan-overview/Purchase-Overview.webp"}
                alt="Happy family planning a room addition with Aham Housing Finance home extension loan at attractive interest rates"
                className="rounded-2xl shadow-large w-full"
              />
            </motion.div>

          </div>
        </div>
      </section>






      {/* Key Benefits Section */}
      <section id="benefits" className="relative py-24 overflow-hidden">

        {/* 🔥 Background – gradient + subtle pattern */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-1"
          style={{
            backgroundImage:
              "url('/img/construction-loan/Key-Benefits.webp')",
          }}
        ></div>

        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/100 via-white/70 to-white"></div>

        <div className="relative container mx-auto px-4">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16 px-4"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-white drop-shadow-lg mb-6 leading-tight">
              {benefitsSettings?.section_title || "Key Benefits & Features"}
            </h2>
            <p className="text-lg md:text-xl text-white mt-4 max-w-3xl mx-auto leading-relaxed font-medium">
              {benefitsSettings?.section_subtitle || "Discover why Aham Home Extension Loan is the right choice for expanding your home"}
            </p>
          </motion.div>

          {/* Benefit Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {(benefitsList !== null ? benefitsList : benefits).map((benefit, index) => {
              const IconComponent = getIconComponent(benefit.icon_name);
              const isDynamic = !!benefit.icon_name;

              return (
                <motion.div
                  key={typeof benefit.id !== 'undefined' ? benefit.id : index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -12, scale: 1.03 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="relative bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
                >
                  {/* Floating Glow */}
                  <div className="absolute -top-4 -right-4 w-20 h-20 bg-primary-500 rounded-full blur-3xl opacity-20"></div>

                  {/* Icon */}
                  <div className={`
                  w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg
                  bg-gradient-to-br 
                  ${benefit.gradient_from?.startsWith('from-') ? benefit.gradient_from : `from-${benefit.gradient_from || 'primary-600'}`} 
                  ${benefit.gradient_to?.startsWith('to-') ? benefit.gradient_to : `to-${benefit.gradient_to || 'primary-400'}`}
                `}>
                    {isDynamic ? <IconComponent className="h-9 w-9 text-white" /> : <benefit.icon className="h-9 w-9 text-white" />}
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-700 leading-relaxed font-medium">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Button */}
          <div className="mt-16 flex justify-center">
            <a href={benefitsSettings?.cta_link || "/contact"} className="w-full flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                {benefitsSettings?.cta_text || "Request a Quote"}
              </motion.button>
            </a>
          </div>
        </div>
      </section>

      {/* Eligibility Criteria Section */}
      <section id="eligibility" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Soft Background Blobs */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary-200/30 blur-3xl rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">

            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-block px-5 py-2 bg-primary-100 text-primary-700 rounded-full font-medium shadow-sm mb-6"
            >
              Loan Eligibility
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-6 mb-4 leading-tight tracking-tight"
            >
              {eligibilitySettings?.section_title || "Eligibility Criteria"}
            </motion.h2>

            <p className="text-lg md:text-xl text-gray-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              {eligibilitySettings?.section_subtitle || "Find out if you're eligible for Aham Home Construction Loan"}
            </p>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {(eligibilityList !== null ? eligibilityList : eligibility).map((item, index) => {
              // Determine content based on whether it's dynamic (object) or static (string)
              const isDynamic = typeof item === 'object';
              const text = isDynamic ? item.criteria : item;

              // Resolve Icon and Gradient
              let IconComp, fromColor, toColor;

              if (isDynamic) {
                IconComp = getIconComponent(item.icon_name);
                // Handle gradients: if stored as 'blue-500', prepend 'from-'/'to-'. If 'from-blue-500', use as is.
                fromColor = item.gradient_from?.startsWith('from-') ? item.gradient_from : `from-${item.gradient_from || 'blue-500'}`;
                toColor = item.gradient_to?.startsWith('to-') ? item.gradient_to : `to-${item.gradient_to || 'cyan-400'}`;
              } else {
                // Static Fallback logic
                // Avoid index out of bounds if eligibility array > icons array (though unlikely in static)
                const iconData = icons[index % icons.length];
                IconComp = () => iconData.icon; // Wrap pre-rendered component? No, iconData.icon IS the element <User/>.
                // Wait, icons array has { icon: <User />, ... }.
                // If I return () => <User/> it works as component.
                // Or just render it directly?
                // The loop below uses <IconComp />.
                // So I should set IconComp to null and render explicitly?
                fromColor = iconData.from;
                toColor = iconData.to;
              }

              return (
                <motion.div
                  key={isDynamic ? item.id : index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="
                group backdrop-blur-xl bg-white/70 
                border border-white/40 shadow-xl rounded-2xl 
                p-6 flex flex-col items-center text-center gap-5 
                hover:-translate-y-2 hover:shadow-2xl 
                transition-all duration-300
              "
                >
                  {/* Gradient Icon */}
                  <div
                    className={`
                  w-14 h-14 rounded-xl flex items-center justify-center 
                  bg-gradient-to-br ${fromColor} ${toColor}
                  text-white shadow-lg group-hover:scale-105 transition
                `}
                  >
                    {isDynamic ? <IconComp className="h-7 w-7" /> : icons[index % icons.length].icon}
                  </div>

                  <div className="flex-1">
                    <p className="text-gray-800 font-semibold text-lg leading-snug">
                      {text}
                    </p>
                  </div>
                </motion.div>
              )
            })}
          </div>

          {/* CTA Button */}
          {/* <div className="text-center mt-16">
            <a href="/customer-360-eligibility-calculator">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="
              px-10 py-4 rounded-full text-white font-semibold 
             bg-[#ed2636]
              shadow-lg hover:bg-primary-600 transition
            "
              >
                Check Your Eligibility Now
              </motion.button>
            </a>
          </div> */}

        </div>
      </section>

      {/* Documents Required Section */}
      <section id="documents" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Background blur circles */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-200/20 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-extrabold text-gray-900 mb-4">
              {documentsSettings?.section_title || "Documents Required"}
            </h2>
            <p className="text-xl text-gray-600">
              {documentsSettings?.section_subtitle || "Keep these documents ready for a smooth application process"}
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto">

            {/* Premium Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
                { key: "common", label: "Common", icon: <FileText /> },
                { key: "salaried", label: "Salaried", icon: <Briefcase /> },
                { key: "selfEmployed", label: "Self-Employed", icon: <User /> },
                { key: "nri", label: "NRI", icon: <Globe /> },
                { key: "property", label: "Property", icon: <Home /> },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
              flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 backdrop-blur-xl
              border border-white/40 shadow-lg
              ${activeTab === tab.key
                      ? "bg-gradient-to-r from-primary-600 to-primary-500 text-white scale-105 shadow-primary/50"
                      : "bg-white/70 text-gray-700 hover:bg-white/90"
                    }
            `}
                >
                  <span className="text-lg">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content Card */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="backdrop-blur-xl bg-white/70 p-10 rounded-3xl border border-white/40 shadow-xl"
            >
              <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(documentsList !== null
                  ? documentsList.filter(d => d.category === activeTab)
                  : documents[activeTab] || []
                ).map((doc, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    className="
                flex items-start gap-4 
                p-5 bg-gradient-to-br from-white to-gray-50 
                rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-200
              "
                  >
                    <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center shadow-inner">
                      <FileText className="h-6 w-6" />
                    </div>
                    <span className="text-gray-900 font-semibold">
                      {typeof doc === 'string' ? doc : doc.document_name}
                    </span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>

          </div>
        </div>
      </section>


      {/* Interest Rates Section */}
      <section
        id="interest"
        className="relative py-24 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white overflow-hidden"
      >
        {/* Decorative Blurs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl opacity-30"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto text-center"
          >
            {/* Title */}
            <h2 className="text-5xl md:text-6xl font-extrabold mb-8 leading-tight drop-shadow-xl">
              {interestSettings?.section_title || "Affordable Home Loan"}
              <span className="block text-primary-200 mt-2">
                {interestSettings?.section_subtitle || "Interest Rates"}
              </span>
            </h2>

            {/* Description Section */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-10 md:p-14 shadow-2xl">
              <p className="text-xl text-white/90 leading-relaxed mb-6">
                {interestSettings?.intro_text || "Aham Housing Finance brings you transparent, competitive, and customer-friendly interest rates designed to make home construction affordable and stress-free."}
              </p>

              <div className="grid md:grid-cols-3 gap-8 my-10">
                {(interestRates.length > 0 ? interestRates : [
                  { icon: "🏠", title: "Flexible Rates", description: "Rates tailored based on loan amount, tenure, credit profile & location." },
                  { icon: "🔍", title: "Transparency", description: "No hidden fees. No surprise charges. Complete clarity from start to finish." },
                  { icon: "🤝", title: "Support", description: "Our expert team guides you at every stage of your construction journey." }
                ]).map((rate, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 + idx * 0.2 }}
                    className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                  >
                    <div className="text-3xl mb-3">{rate.icon}</div>
                    <h4 className="text-xl font-semibold mb-2">{rate.title}</h4>
                    <p className="text-white/80 text-sm leading-relaxed">
                      {rate.description}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Final Message */}
              <p className="text-lg text-white/85 mb-10 leading-relaxed">
                {interestSettings?.closing_text || "Begin your home construction journey with confidence and clarity. Aham ensures the process is smooth, transparent, and tailored to your needs."}
              </p>

              {/* CTA Button */}
              {/* <motion.a
                href={interestSettings?.cta_link || "https://aham.mbwhost.in/emi-calculator"}
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white-700 hover:text-black hover:bg-primary-50 px-10 py-4 rounded-xl font-semibold text-xl transition-all duration-300 shadow-xl"
              >
                {interestSettings?.cta_text || "Calculate EMI"}
              </motion.a> */}
            </div>
          </motion.div>
        </div>
      </section>


      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {faqSettings?.section_title || "Frequently Asked Questions"}
            </h2>
            <p className="text-xl text-gray-600">
              {faqSettings?.section_subtitle || "Find answers to common questions about Home Extension Loan"}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {(faqsList !== null ? faqsList : faqs).map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="mb-4"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className={`w-full text-left p-6 rounded-xl transition-all duration-300 ${activeFAQ === index
                    ? 'bg-primary-50 border-2 border-primary-600'
                    : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    {activeFAQ === index ? (
                      <ChevronUpIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
                    ) : (
                      <ChevronDownIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                  {activeFAQ === index && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 text-gray-700 leading-relaxed"
                    >
                      {faq.answer}
                    </motion.p>
                  )}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(img/construction-loan/home-construction-loan.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {ctaBanner?.title || "Ready to Expand Your Dream Home?"}
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto text-white/90">
              {ctaBanner?.description || "Take the first step towards extending your living space with Aham's hassle-free loan process"}
            </p>
            <div className="flex justify-center">
              <motion.a
                href={ctaBanner?.button_link || "https://aham.mbwhost.in/contact"}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white hover:bg-white hover:text-primary-600 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                {ctaBanner?.button_text || "Talk to Our Loan Expert"}
              </motion.a>
            </div>

          </motion.div>
        </div>
      </section>


    </div>
  );
};

export default HomeExtensionLoan;